/**
 * BuyboxEditPage - Form for editing an existing buybox.
 *
 * Loads the buybox data and renders BuyboxForm in edit mode.
 */

import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { useAuth } from '@/common/hooks/useAuth'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Icon } from '@/common/components/icon'
import { Stack } from '@/common/components/stack'
import { useMarkets } from '@/common/hooks/useMarkets'
import { ApiError } from '@/common/api/client'
import { getBuybox } from '@/features/buyboxes/api/buyboxes'
import { BuyboxForm } from '@/features/buyboxes/components/BuyboxForm'

import * as styles from './edit.css'

export const Route = createFileRoute({
  component: BuyboxEditPage,
})

function BuyboxEditPage() {
  const { id } = useParams({ strict: false })
  const navigate = useNavigate()
  const { token } = useAuth()
  const { investorMarketOptions } = useMarkets({ includeUpcoming: true })

  // Fetch buybox data
  const {
    data: buybox,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['buybox', id],
    queryFn: () => getBuybox(token!, id!),
    enabled: !!token && !!id,
  })

  const handleSuccess = () => {
    navigate({ to: '/buybox' })
  }

  const handleCancel = () => {
    navigate({ to: '/buybox' })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state - differentiate between access denied (403) and not found (404)
  if (error || !buybox) {
    const isAccessDenied = error instanceof ApiError && error.status === 403

    return (
      <div className={styles.container}>
        <Link to="/buybox" className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back to Buyboxes
        </Link>
        <div className={styles.errorContainer}>
          <Stack gap="8" align="center">
            <Text as="h2" variant="bodyLg" weight="bold">
              {isAccessDenied ? 'Access Restricted' : 'Buybox Not Found'}
            </Text>
            <Text variant="body" color="muted">
              {isAccessDenied
                ? "You don't have permission to view or edit this buybox."
                : "The buybox you're looking for doesn't exist."}
            </Text>
          </Stack>
        </div>
      </div>
    )
  }

  return (
    <BuyboxForm
      mode="edit"
      initialData={buybox}
      marketOptions={investorMarketOptions}
      onSuccess={handleSuccess}
      onCancel={handleCancel}
    />
  )
}
